import { FunctionComponent, useCallback, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import styled, { css, useTheme } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import { triggerAureusImpression } from '@utils/aureus'
import { Image, Link, TargetContentType, TeaserVideo } from '@utils/cook/types'
import { LIVE_EVENT_DATA_SPORT_TYPES } from '@utils/formatters/common'
import { LiveEventDataSportTypes } from '@utils/formatters/liveEvent'
import useAnimatedPreview from '@hooks/useAnimatedPreview'
import useAureusDeboost from '@hooks/useAureusDeboost'
import useClientViewportType from '@hooks/useClientViewportType'
import useIsHybridTeaser from '@hooks/useIsHybridTeaser'
import useRefDelegate from '@hooks/useRefDelegate'
import useSportsEvent from '@hooks/useSportsEvent'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import useViewportTracking from '@hooks/useViewportTracking'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import HockeySoccerSportsFooter from '@components/SportsFooter/HockeySoccer'
import SportLabelContent from '@components/SportsFooter/SportLabelContent'
import { transformDataForSportsComponents } from '@components/SportsFooter/utils'
import TeaserAnimatedPreview from '@components/TeaserAnimatedPreview'
import BlickPlusLogo from '@components/TeaserStandardVertical/BlickPlusLogo'
import Catchword from '@components/TeaserStandardVertical/Catchword'
import ImageComponent from '@components/TeaserStandardVertical/Image'
import Label from '@components/TeaserStandardVertical/Label'
import LoudSpeaker from '@components/TeaserStandardVertical/LoudSpeaker'
import PromoLabel from '@components/TeaserStandardVertical/PromoLabel'
import SponsoredLabel from '@components/TeaserStandardVertical/SponsoredLabel'
import SponsoredLogo from '@components/TeaserStandardVertical/SponsoredLogo'
import Title from '@components/TeaserStandardVertical/Title'
import StyledTeaserStandardVerticalWrapper from '@components/TeaserStandardVertical/Wrapper'
import VideoLabel from '@components/VideoLabel'

export interface TeaserStandardVerticalCommonProps {
  layoutTemplate?: 'default' | 'compact'
  accentColor: string
  title: string
  catchword?: string
  isPlus?: boolean
  articleId?: string
  track?: boolean
  link: Link
  image: Image<'3_2'>
  section: {
    name: string
    uniqueName: string
  }
  contentOrigin?: string
  targetContentType: TargetContentType
  video?: TeaserVideo
  aureusOfferId?: string
  hasSpecialLabel?: boolean
}

export interface TeaserStandardVerticalNoCommercialProps {
  commercialType?: undefined
  labelText?: string
}

export interface TeaserStandardVerticalPromoCommercialProps {
  commercialType: 'promo'
  commercial: {
    label: string
  }
}

export interface TeaserStandardVerticalSponsoredCommercialProps {
  commercialType: 'sponsored' | 'editorial'
  commercial: {
    label: string
    logo?: Image<'free'>
  }
}

export type TeaserStandardVerticalMaybeCommercialProps =
  | TeaserStandardVerticalNoCommercialProps
  | TeaserStandardVerticalPromoCommercialProps
  | TeaserStandardVerticalSponsoredCommercialProps

export interface TeaserStandardVerticalNoSportProps {
  typeOfSport?: undefined
}

export interface TeaserStandardVerticalSportProps {
  typeOfSport: Extract<LiveEventDataSportTypes, 'soccer' | 'hockey'>
  matchId: string
}

export type TeaserStandardVerticalMaybeSportProps =
  | TeaserStandardVerticalNoSportProps
  | TeaserStandardVerticalSportProps

export type TeaserStandardVerticalAPIProps = TeaserStandardVerticalCommonProps &
  TeaserStandardVerticalMaybeCommercialProps &
  TeaserStandardVerticalMaybeSportProps

export interface StyledImageComponentProps {
  hasBorder: boolean
}

export interface StyledVideoLabelProps {
  isCompact: boolean
}

const StyledImageComponent = styled(ImageComponent)<StyledImageComponentProps>`
  ${({
    theme: {
      spacing: { spacing8 },
      colors: { staticWhite },
    },
    hasBorder,
  }) => css`
    margin-bottom: ${spacing8};
    ${hasBorder &&
    css`
      &:before {
        content: '';
        position: absolute;
        top: ${spacing8};
        right: ${spacing8};
        bottom: ${spacing8};
        left: ${spacing8};
        border: 1px solid ${staticWhite};
        z-index: 1;
      }
    `}
  `};
`

const StyledPromoLabel = styled(PromoLabel)`
  ${({
    theme: {
      spacing: { spacing20 },
    },
  }) => css`
    position: absolute;
    top: ${spacing20};
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
  `}
`

const StyledVideoLabel = styled(VideoLabel)<StyledVideoLabelProps>`
  ${({
    theme: {
      spacing: { spacing16, spacing24 },
    },
    isCompact,
  }) => css`
    position: absolute;
    z-index: 1;
    bottom: ${spacing16};
    left: ${spacing16};
    ${desktopCSS(css`
      bottom: ${isCompact ? spacing16 : spacing24};
      left: ${isCompact ? spacing16 : spacing24};
    `)};
  `}
`

const StyledSportsFooter = styled(HockeySoccerSportsFooter)<{
  isCompact: boolean
}>`
  ${({ isCompact }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    ${!isCompact &&
    desktopCSS(css`
      height: 72px;
    `)}
  `}
`

const TeaserStandardVertical: FunctionComponent<
  TeaserStandardVerticalAPIProps
> = (props) => {
  const {
    accentColor,
    title,
    layoutTemplate,
    link,
    image,
    isPlus,
    catchword,
    commercialType,
    targetContentType,
    typeOfSport,
    articleId,
    contentOrigin,
    track,
    video,
    aureusOfferId,
    hasSpecialLabel,
  } = props

  const {
    colors: { fillSport },
  } = useTheme()
  const isEditorial = commercialType === 'editorial'

  const isCommercial = !!commercialType

  const hasBorder = isCommercial && !isEditorial

  const isPromo = commercialType === 'promo'

  const isSponsoredOrEditorial = isCommercial && !isPromo

  const showCatchword = !!isPlus || !!catchword

  const isHybridTeaser = useIsHybridTeaser()

  const clientViewportType = useClientViewportType()

  const { deboostOnClick } = useAureusDeboost()

  const isValidSport =
    !!typeOfSport &&
    (
      [
        LIVE_EVENT_DATA_SPORT_TYPES.SOCCER,
        LIVE_EVENT_DATA_SPORT_TYPES.HOCKEY,
      ] as LiveEventDataSportTypes[]
    ).includes(typeOfSport)

  const { data: sportsEventData } = useSportsEvent({
    matchId: isValidSport ? props.matchId : '',
    typeOfSport: isValidSport ? typeOfSport : 'NO_SPORT',
  })

  const sportsData = sportsEventData
    ? transformDataForSportsComponents(sportsEventData)
    : undefined

  const shouldShowSportsComponents =
    sportsData?.typeOfSport === LIVE_EVENT_DATA_SPORT_TYPES.SOCCER ||
    sportsData?.typeOfSport === LIVE_EVENT_DATA_SPORT_TYPES.HOCKEY

  const labelColor = sportsData?.isLive
    ? undefined
    : isValidSport
      ? fillSport
      : accentColor

  const hasLabelText = !isCommercial && !!props.labelText
  const hasLoudspeaker = hasLabelText && !!hasSpecialLabel

  const queryClient = useQueryClient()

  const onImpression = useCallback<TrackingFnType>(() => {
    return {
      event: 'teaser_impression',
      eventCategory: video ? 'video_teaser' : 'article_teaser',
      eventLabel: `${articleId}:${stripHtml(title)}`,
      eventAction: 'impression',
      id: articleId,
      is_branded: !!contentOrigin,
      link_url: link.href,
    }
  }, [articleId, contentOrigin, title, video, link.href])

  const onPromoImpression = useCallback<TrackingFnType>(
    () => ({
      event: 'contentcommerce_impression',
      eventCategory: 'BrandStudio',
      eventAction: 'Impression / Brandstudio',
    }),
    [],
  )

  const onClick = useCallback<TrackingFnType>(() => {
    return {
      event: 'teaser_click',
      eventCategory: video ? 'video_teaser' : 'article_teaser',
      eventLabel: `${articleId}:${stripHtml(title)}`,
      eventAction: 'click',
      id: articleId,
      is_branded: !!contentOrigin,
      link_url: link.href,
    }
  }, [articleId, contentOrigin, title, video, link.href])

  const trackedOnImpression = useTracking(onImpression)
  const trackedOnPromoImpression = useTracking(onPromoImpression)
  const trackedOnClick = useTracking(onClick)

  const shouldBeTracked = !!contentOrigin || track

  const finalOnImpression = useCallback(() => {
    triggerAureusImpression({
      aureusOfferId,
      queryClient,
    })

    if (shouldBeTracked) {
      trackedOnImpression()
      if (commercialType) {
        trackedOnPromoImpression()
      }
    }
  }, [
    aureusOfferId,
    commercialType,
    queryClient,
    shouldBeTracked,
    trackedOnImpression,
    trackedOnPromoImpression,
  ])

  const trackAndDeboost = useCallback(() => {
    trackedOnClick()
    deboostOnClick({ articleId, aureusOfferId })
  }, [trackedOnClick, deboostOnClick, articleId, aureusOfferId])

  const clickableProps = {
    aureusOfferId,
    ...(shouldBeTracked
      ? { onClick: trackAndDeboost }
      : { onClick: () => deboostOnClick({ articleId, aureusOfferId }) }),
    ...link,
  }

  const animatedPreviewRef = useAnimatedPreview({
    video,
    articleUrl: clickableProps.href,
  })

  const viewportTrackingRef = useViewportTracking({
    onImpression: finalOnImpression,
    track: true,
  })

  const ref = useRefDelegate(
    useMemo(
      () => [animatedPreviewRef, viewportTrackingRef],
      [animatedPreviewRef, viewportTrackingRef],
    ),
  )

  if (isHybridTeaser && !['server', 'desktop'].includes(clientViewportType)) {
    return null
  }

  return (
    <StyledTeaserStandardVerticalWrapper
      {...clickableProps}
      isHybridTeaser={isHybridTeaser}
      ref={ref}
    >
      {hasLoudspeaker && hasLabelText && (
        <LoudSpeaker labelColor={labelColor}>{props.labelText}</LoudSpeaker>
      )}
      <StyledImageComponent
        {...image}
        hasBorder={hasBorder}
        layoutTemplate={layoutTemplate}
      >
        {isPromo ? (
          <StyledPromoLabel>{props.commercial.label}</StyledPromoLabel>
        ) : null}
        {!!video && (
          <TeaserAnimatedPreview video={video} articleUrl={link.href} />
        )}
        {!!props.video && (
          <StyledVideoLabel
            isCompact={layoutTemplate === 'compact'}
            duration={props.video.duration}
            targetContentType={targetContentType}
          />
        )}
        {shouldShowSportsComponents && (
          <StyledSportsFooter
            isCompact={layoutTemplate === 'compact'}
            sportsData={sportsData}
            viewType="full"
          />
        )}
      </StyledImageComponent>
      {!hasLoudspeaker && (hasLabelText || isValidSport) && (
        <Label labelColor={labelColor}>
          {shouldShowSportsComponents ? (
            <SportLabelContent sportsData={sportsData} />
          ) : hasLabelText ? (
            props.labelText
          ) : null}
        </Label>
      )}
      {isSponsoredOrEditorial && (
        <SponsoredLabel>
          {props.commercial.label}
          {!!props.commercial.logo && (
            <SponsoredLogo {...props.commercial.logo} />
          )}
        </SponsoredLabel>
      )}
      {showCatchword && (
        <Catchword>
          {isPlus && <BlickPlusLogo />}
          {catchword}
        </Catchword>
      )}
      <Title layoutTemplate={layoutTemplate}>{title}</Title>
    </StyledTeaserStandardVerticalWrapper>
  )
}

const widget = {
  kind: ['teaser-standard-vertical'],
  component: TeaserStandardVertical,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
